import { ExternalLink, Loader2, Pause, PlayCircle, Server } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { useHostingInfo, useRestoreHosting, useSuspendHosting } from "@/hooks/useHosting";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import { cn, formatStorage } from "@/lib/utils";

interface HostingPanelProps {
  containerId: number;
  twentyiPackageId: string;
}

/**
 * Shown on Overview when the container HAS a TwentyIPackageId. Pulls live
 * package state from 20i (disk + bandwidth) and exposes suspend/restore.
 */
export function HostingPanel({ containerId, twentyiPackageId }: HostingPanelProps) {
  const { data, isLoading, error } = useHostingInfo(containerId, true);

  if (isLoading) return <Skel />;
  if (error || !data) return <ErrorCard error={error} />;

  const status = normalizeStatus(data.status);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <StatusIcon status={status} />
          <div>
            <CardTitle className="text-lg">Hosting</CardTitle>
            <CardDescription>
              {data.platform ? `${capitalize(data.platform)} on 20i` : "Managed on 20i"}
            </CardDescription>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={status} />
          <SuspendRestoreButton containerId={containerId} status={status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-2">
          <UsageRow
            label="Storage"
            used={data.storage_mb_used / 1024}
            limit={data.storage_mb / 1024}
            unit="GB"
          />
          <UsageRow
            label="Bandwidth"
            used={data.bandwidth_mb_used / 1024}
            limit={data.bandwidth_mb / 1024}
            unit="GB"
          />
        </div>

        <a
          href={`https://my.20i.com/packages/${twentyiPackageId}`}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open in 20i Control Panel
        </a>
      </CardContent>
    </Card>
  );
}

// ── Bits ──────────────────────────────────────────────────
function UsageRow({ label, used, limit, unit }: { label: string; used: number; limit: number; unit: string }) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const overUsage = pct >= 90;
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground">
          <span className={cn("font-semibold", overUsage ? "text-destructive" : "text-foreground")}>
            {formatStorage(used)}
          </span>{" "}
          / {limit > 0 ? formatStorage(limit) : "—"} {unit === "GB" && limit > 0 ? "" : ""}
        </span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full", overUsage ? "bg-destructive" : "bg-primary")}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: "active" | "suspended" | "provisioning" | "unknown" }) {
  const wrap = "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg";
  if (status === "active")       return <div className={cn(wrap, "bg-brand-greenSoft text-brand-green")}><Server className="h-5 w-5" /></div>;
  if (status === "suspended")    return <div className={cn(wrap, "bg-brand-gold/10 text-brand-gold")}><Pause className="h-5 w-5" /></div>;
  if (status === "provisioning") return <div className={cn(wrap, "bg-brand-accentSoft text-primary")}><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return <div className={cn(wrap, "bg-muted text-muted-foreground")}><Server className="h-5 w-5" /></div>;
}

function StatusBadge({ status }: { status: "active" | "suspended" | "provisioning" | "unknown" }) {
  if (status === "active")       return <Badge variant="success" className="uppercase">Active</Badge>;
  if (status === "suspended")    return <Badge variant="outline" className="border-brand-gold/40 bg-brand-gold/10 text-brand-gold uppercase">Suspended</Badge>;
  if (status === "provisioning") return <Badge variant="soft" className="uppercase">Provisioning</Badge>;
  return <Badge variant="outline" className="uppercase">{status}</Badge>;
}

function SuspendRestoreButton({ containerId, status }: {
  containerId: number; status: "active" | "suspended" | "provisioning" | "unknown";
}) {
  const suspend = useSuspendHosting(containerId);
  const restore = useRestoreHosting(containerId);
  const { toast } = useToast();

  async function doSuspend() {
    try {
      await suspend.mutateAsync();
      toast({ title: "Hosting suspended", variant: "success" });
    } catch (err) {
      toast({
        title: "Suspend failed",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }
  async function doRestore() {
    try {
      await restore.mutateAsync();
      toast({ title: "Hosting restored", variant: "success" });
    } catch (err) {
      toast({
        title: "Restore failed",
        description: err instanceof ApiError ? err.message : "Network error.",
        variant: "destructive"
      });
    }
  }

  if (status === "suspended") {
    return (
      <Button size="sm" variant="outline" onClick={doRestore} disabled={restore.isPending}>
        {restore.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
        Restore
      </Button>
    );
  }

  if (status === "active") {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="sm" variant="outline" disabled={suspend.isPending}>
            {suspend.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pause className="h-4 w-4" />}
            Suspend
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspend hosting?</AlertDialogTitle>
            <AlertDialogDescription>
              Visitors will see a "site suspended" page. Mailboxes keep working. Undo via "Restore" anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doSuspend}>Suspend</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return null;   // provisioning / unknown — no action surfaced
}

function Skel() {
  return (
    <Card><CardContent className="space-y-3 py-5">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="h-2 w-full" />
      <Skeleton className="h-2 w-full" />
    </CardContent></Card>
  );
}

function ErrorCard({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError ? `${error.message} (${error.code ?? error.status})` : "Network error.";
  return (
    <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">
      Couldn't load hosting info: {message}
    </CardContent></Card>
  );
}

// ── Helpers ────────────────────────────────────────────────
function normalizeStatus(raw: string): "active" | "suspended" | "provisioning" | "unknown" {
  const v = (raw ?? "").toLowerCase();
  if (v === "active")       return "active";
  if (v === "suspended")    return "suspended";
  if (v === "provisioning") return "provisioning";
  return "unknown";
}

function capitalize(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }
